import produce from 'immer';
import {v4 as uuidv4} from 'uuid';

import * as Analytics from 'analytics/Analytics';
import {standardAnalyticsMetadataToDict} from 'analytics/Util';
import {assert} from 'Asserts';
import {validateCompleteBenchlingExpression} from 'benchling-expressions/BenchlingExpressionUtils';
import {ActionOrigin, ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {FlowchartActionFunction} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import {
  ProcessFlowchartInteractiveEdge,
  ProcessFlowchartInteractiveNode,
  ProcessInteractiveFlowchart,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import findUpstreamTaskNodes from 'processes/graph-builder-processes/flowchart-viz/util/findUpstreamTaskNodes';
import getSchemaOfItemsBeingRouted from 'processes/graph-builder-processes/flowchart-viz/util/getSchemaOfItemsBeingRouted';
import {
  ProcessFlowchartConfigForGraphBuilder,
  ProcessTaskSchemaForGraphBuilderValidation,
} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {
  CreateOrUpdateRouterFunctionSpec,
  ROUTER_CONFIGURATION_MODAL_TYPE,
  RouterConfig,
  UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_SPEC,
  UNCONFIGURED_NON_DEFAULT_ROUTER_FUNCTION_SPEC,
} from 'processes/modals/router-config/RouterConfigConstants';
import RouterConfigModal from 'processes/modals/router-config/RouterConfigModal';
import {getSchemasReferencedInRoutingRules} from 'processes/modals/router-config/RouterConfigUtils';
import legacyRunModal from 'ui-library/legacyRunModal';

import {FlowchartAnalyticsMetadata} from '../ProcessFlowchartEditor';
import {ProcessFlowchartRouterNode} from '../ProcessFlowchartNodeDetailType';
import convertProcessFlowchartInteractiveNodesToProcessGraphBuilderNodes from '../util/convertProcessFlowchartNodesToGraphBuilderNodes';
import getAvailableDownstreamTaskNodesForRouterNode from '../util/getAvailableDownstreamTaskNodesForRouterNode';
import getImmediateChildNodesForRouterNode from '../util/getImmediateChildNodesForRouterNode';
import {isRootNode, isRouterNode, isTaskNode} from '../util/getNodeDetailsTypeForInteractiveNode';
import {deleteNodeFromFlowchart} from './deleteNodeProcessFlowchartActionFunction';

const MIN_ROUTER_INBOUND_EDGES = 1;
const MAX_ROUTER_INBOUND_EDGES = 1;
const MIN_ROUTER_OUTBOUND_EDGES = 2;

export function updateRouterConfigInFlowchart(
  routerNode: ProcessFlowchartInteractiveNode,
  flowchart: ProcessInteractiveFlowchart,
  updatedRouterConfig: RouterConfig
): ProcessInteractiveFlowchart {
  return produce(flowchart, (draftFlowchart) => {
    const draftRouterNode = draftFlowchart.nodes.find((node) => node.clientId === routerNode.clientId);
    assert(
      draftRouterNode != null && isRouterNode(draftRouterNode),
      `Expected router node ${routerNode.clientId} to be present in flowchart.`
    );
    draftRouterNode.routerConfig = updatedRouterConfig;
    draftRouterNode.name = updatedRouterConfig.name;
  });
}

export function addUnconfiguredFunctionsForRouterConfig(
  routerNode: ProcessFlowchartRouterNode,
  numEdgesWithoutFunctionSpecs: number,
  numRouterOutboundEdges: number
): RouterConfig {
  const newFunctionSpecs: Array<CreateOrUpdateRouterFunctionSpec> = [];
  const numNewFunctionSpecsNeeded =
    routerNode.routerConfig.defaultFunctionSpec == null
      ? numEdgesWithoutFunctionSpecs - 1
      : numEdgesWithoutFunctionSpecs;
  for (let index = 0; index < numNewFunctionSpecsNeeded; index++) {
    newFunctionSpecs.push({
      ...UNCONFIGURED_NON_DEFAULT_ROUTER_FUNCTION_SPEC,
      name: `Route ${numRouterOutboundEdges - numNewFunctionSpecsNeeded + index}`,
      clientId: uuidv4(),
    });
  }
  return {
    ...routerNode.routerConfig,
    functionSpecs: routerNode.routerConfig.functionSpecs.concat(newFunctionSpecs),
    defaultFunctionSpec:
      routerNode.routerConfig.defaultFunctionSpec == null
        ? {...UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_SPEC, clientId: uuidv4()}
        : routerNode.routerConfig.defaultFunctionSpec,
  };
}

export function setUnconfiguredFunctionsForRouterConfig(
  routerNode: ProcessFlowchartInteractiveNode,
  routerNodeOutboundEdges: Array<ProcessFlowchartInteractiveEdge>
): RouterConfig {
  const nonDefaultFunctionSpecs = routerNodeOutboundEdges.map((edge, index) => ({
    ...UNCONFIGURED_NON_DEFAULT_ROUTER_FUNCTION_SPEC,
    name: `Route ${index + 1}`,
    clientId: uuidv4(),
  }));
  // we'll have one extra outbound edge for the default function
  nonDefaultFunctionSpecs.pop();
  return {
    clientId: routerNode.clientId ?? uuidv4(),
    name:
      routerNode.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS
        ? routerNode.routerConfig.name
        : routerNode.name,
    defaultFunctionSpec: {...UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_SPEC, clientId: uuidv4()},
    functionSpecs: nonDefaultFunctionSpecs,
  };
}

function hasUnconfiguredFunctions(routerConfig: RouterConfig): boolean {
  return (
    routerConfig.functionSpecs.some(
      (functionSpec: CreateOrUpdateRouterFunctionSpec) => !functionSpec.isConfigured
    ) || !routerConfig.defaultFunctionSpec?.isConfigured
  );
}

export function getNumOutboundEdgesWithoutFunctions(
  routerNode: ProcessFlowchartRouterNode,
  numRouterNodeOutboundEdges: number
): number {
  const numExistingFunctionSpecs =
    routerNode.routerConfig.functionSpecs.length +
    (routerNode.routerConfig.defaultFunctionSpec != null ? 1 : 0);
  const numEdgesWithoutFunctionSpecs = numRouterNodeOutboundEdges - numExistingFunctionSpecs;
  assert(
    numEdgesWithoutFunctionSpecs >= 0 && numEdgesWithoutFunctionSpecs <= numRouterNodeOutboundEdges,
    `Found unexpected number of edges without funtion specs for router node ${routerNode.clientId}`
  );

  return numEdgesWithoutFunctionSpecs;
}

export function setExpressionsAndConfiguredFlagForRouterConfig(routerConfig: RouterConfig): RouterConfig {
  return produce(routerConfig, (draftRouterConfig) => {
    const defaultFunctionSpec = draftRouterConfig.defaultFunctionSpec;
    assert(
      defaultFunctionSpec != null,
      `Unexpectedly found null default function spec for router config ${routerConfig.clientId}.`
    );
    defaultFunctionSpec.isConfigured = true;
    assert(
      routerConfig.functionSpecs.every(
        (functionSpec) =>
          functionSpec.expression != null && validateCompleteBenchlingExpression(functionSpec.expression)
      ),
      `Unexpectedly found incomplete function spec.`
    );
    for (const fSpec of draftRouterConfig.functionSpecs) {
      fSpec.isConfigured = true;
    }
  });
}

export function isRouterNodeConfigurationDisabled(
  item: ProcessFlowchartRouterNode,
  flowchart: ProcessInteractiveFlowchart,
  flowchartSchema: ProcessTaskSchemaForGraphBuilderValidation,
  flowchartConfig: ProcessFlowchartConfigForGraphBuilder
): boolean {
  const routerOutboundEdges = flowchart.edges.filter((edge) => edge.fromNodeClientId === item.clientId);
  const routerInboundEdges = flowchart.edges.filter((edge) => edge.toNodeClientId === item.clientId);

  const areMappersConfiguredBetweenUpstreamAndDownstreamNodes = (): boolean => {
    const inputNodes = findUpstreamTaskNodes(item, flowchart);
    if (inputNodes.length !== 1) {
      return false;
    }
    const availableDownstreamTaskNodes = getAvailableDownstreamTaskNodesForRouterNode(
      flowchart,
      inputNodes[0],
      item,
      flowchartSchema
    );

    if (inputNodes.length !== 1) {
      return false;
    }
    const inputNode = inputNodes[0];
    if (!isTaskNode(inputNode) && !isRootNode(inputNode)) {
      return false;
    }

    const inputGraphBuilderNodes = convertProcessFlowchartInteractiveNodesToProcessGraphBuilderNodes(
      inputNodes,
      flowchartSchema
    );
    assert(
      inputGraphBuilderNodes[0].type === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
        inputGraphBuilderNodes[0].type === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
      'Node must be task type'
    );

    const schemaId = getSchemaOfItemsBeingRouted(
      inputGraphBuilderNodes[0],
      availableDownstreamTaskNodes,
      flowchartConfig.schemaMapperConfigs
    );

    if (schemaId == null) {
      return false;
    }

    const schemaIdsReferencedInRoutingRules = getSchemasReferencedInRoutingRules(item.routerConfig);
    if (schemaIdsReferencedInRoutingRules.length > 1) {
      return false;
    } else if (schemaIdsReferencedInRoutingRules.length === 1) {
      return schemaIdsReferencedInRoutingRules[0] === schemaId;
    }

    return true;
  };

  if (routerOutboundEdges.length < MIN_ROUTER_OUTBOUND_EDGES) {
    return true;
  } else if (routerInboundEdges.length < MIN_ROUTER_INBOUND_EDGES) {
    return true;
  } else if (routerInboundEdges.length > MAX_ROUTER_INBOUND_EDGES) {
    return true;
  } else if (!areMappersConfiguredBetweenUpstreamAndDownstreamNodes()) {
    return true;
  }
  return false;
}

export default function configureRouterProcessFlowchartActionFunction({
  flowchart,
  onChangeFlowchart,
  flowchartSchema,
  flowchartConfig,
  editorEventGroupId,
  flowchartAnalyticsMetatdata,
  actionOrigin,
  didInitialFlowchartHaveChainedRouters,
  doesCurrentFlowchartHaveChainedRouters,
}: {
  flowchart: ProcessInteractiveFlowchart;
  onChangeFlowchart: (flowchart: ProcessInteractiveFlowchart) => void;
  flowchartSchema: ProcessTaskSchemaForGraphBuilderValidation;
  flowchartConfig: ProcessFlowchartConfigForGraphBuilder;
  editorEventGroupId: string;
  flowchartAnalyticsMetatdata: FlowchartAnalyticsMetadata;
  actionOrigin: ActionOrigin;
  didInitialFlowchartHaveChainedRouters: boolean;
  doesCurrentFlowchartHaveChainedRouters: boolean;
}): FlowchartActionFunction<ProcessFlowchartInteractiveNode, ProcessInteractiveFlowchart> {
  return {
    icon: 'edit',
    name: 'Configure router',
    tooltipText: 'Configure',
    flowchart,
    updateHandler: onChangeFlowchart,
    getIsDisabled: (item, flowchart) => {
      if (!isRouterNode(item)) {
        return true;
      }
      return isRouterNodeConfigurationDisabled(item, flowchart, flowchartSchema, flowchartConfig);
    },
    onInvoke: ({item, flowchart, updateHandler}) => {
      assert(
        isRouterNode(item),
        `Unexpectedly called router configuration action on non-router node ${item.clientId}.`
      );

      const inputNodes = findUpstreamTaskNodes(item, flowchart);
      if (inputNodes.length !== 1) {
        notifications.error('Cannot configure router with more than one input task node.');
        return;
      }
      const availableDownstreamTaskNodes = getAvailableDownstreamTaskNodesForRouterNode(
        flowchart,
        inputNodes[0],
        item,
        flowchartSchema
      );

      const immediateChildNodes = getImmediateChildNodesForRouterNode(
        flowchart,
        inputNodes[0],
        item,
        flowchartSchema
      );

      const routerNodeOutboundEdges = flowchart.edges.filter(
        (edge) => edge.fromNodeClientId === item.clientId
      );

      const inputGraphBuilderNodes = convertProcessFlowchartInteractiveNodesToProcessGraphBuilderNodes(
        inputNodes,
        flowchartSchema
      );

      let routerConfig = item.routerConfig;
      // TODO (BNCH-59692): Instead of resetting the entire router config when there is an unconfigured part,
      // we should keep the parts of the config that have already been configured.
      if (hasUnconfiguredFunctions(routerConfig)) {
        routerConfig = setUnconfiguredFunctionsForRouterConfig(item, routerNodeOutboundEdges);
      }

      const numEdgesWithoutFunctionSpecs = getNumOutboundEdgesWithoutFunctions(
        item,
        routerNodeOutboundEdges.length
      );
      if (numEdgesWithoutFunctionSpecs > 0) {
        routerConfig = addUnconfiguredFunctionsForRouterConfig(
          item,
          numEdgesWithoutFunctionSpecs,
          routerNodeOutboundEdges.length
        );
      }

      const analyticsMetadata = {
        ...flowchartAnalyticsMetatdata,
        actionOrigin,
        eventGroupId: editorEventGroupId,
      };
      Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
        ...standardAnalyticsMetadataToDict(analyticsMetadata),
        modal_type: ROUTER_CONFIGURATION_MODAL_TYPE,
      });

      legacyRunModal(RouterConfigModal, {
        routerConfig,
        routerNodeClientId: item.clientId,
        inputNodes: inputGraphBuilderNodes,
        flowchartConfig,
        flowchartClientId: flowchart.clientId,
        flowchartSchema,
        didInitialFlowchartHaveChainedRouters,
        doesCurrentFlowchartHaveChainedRouters,
        onUpdateRouterConfig: (newRouterDetails: RouterConfig) => {
          const newRouterConfig = setExpressionsAndConfiguredFlagForRouterConfig(newRouterDetails);
          const newFlowchart = updateRouterConfigInFlowchart(item, flowchart, newRouterConfig);
          updateHandler(newFlowchart);
        },
        onDelete: () => {
          const updatedFlowchart = deleteNodeFromFlowchart(item.clientId, flowchart);
          updateHandler(updatedFlowchart);
        },
        availableDownstreamTaskNodes,
        immediateChildNodes,
        analyticsMetadata,
      });
    },
  };
}
